<template>
  <b-modal
      v-model="modalOpened"
      id="admin-app-notification-modal"
      ref="modal"
      title="App Notification"
      hide-footer
      centered
  >
    <b-form @submit.prevent="submitForm">
      <b-form-group label="Notification Title">
        <b-form-input
            v-model.trim="formData.title"
            type="text"
            :state="fieldValid('title')"
            :disabled="isDisabled"
        />
        <b-form-invalid-feedback :state="fieldValid('title')">
          {{ getFieldError('title') }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Is title hidden?">
        <b-form-checkbox
            v-model="formData.is_title_hidden"
            :state="fieldValid('is_title_hidden')"
            :disabled="isDisabled"
        >
          Yes
        </b-form-checkbox>
        <b-form-invalid-feedback :state="fieldValid('is_title_hidden')">
          {{ getFieldError('is_title_hidden') }}
        </b-form-invalid-feedback>
        <i>Hidden titles are not displayed to the users</i>
      </b-form-group>

      <b-form-group label="Notification Content">
        <vue-editor v-if="!isDisabled" v-model="formData.content" :editor-toolbar="editorToolbar"/>
        <div v-else v-html="formData.content" class="notification-content-preview"/>
        <b-form-invalid-feedback :state="fieldValid('content')">
          {{ getFieldError('content') }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Level">
        <b-dropdown
            :text="levelBtnText"
            block
            :variant="levelBtnVariant"
            menu-class="w-100"
            class="level-btn"
            :disabled="isDisabled"
        >
          <b-dropdown-item @click="formData.level = null">Default</b-dropdown-item>
          <b-dropdown-item @click="formData.level = 'success'" link-class="bg-success level-btn-item">Success</b-dropdown-item>
          <b-dropdown-item @click="formData.level = 'info'" link-class="bg-info level-btn-item">Info</b-dropdown-item>
          <b-dropdown-item @click="formData.level = 'warning'" link-class="bg-warning level-btn-item">Warning</b-dropdown-item>
          <b-dropdown-item @click="formData.level = 'error'" link-class="bg-danger level-btn-item">Error</b-dropdown-item>
        </b-dropdown>
        <b-form-invalid-feedback :state="fieldValid('level')">
          {{ getFieldError('level') }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Is fixed?">
        <b-form-checkbox
            v-model="formData.is_fixed"
            :state="fieldValid('is_fixed')"
            :disabled="isDisabled"
        >
          Yes
        </b-form-checkbox>
        <b-form-invalid-feedback :state="fieldValid('is_fixed')">
          {{ getFieldError('is_fixed') }}
        </b-form-invalid-feedback>
        <i>Fixed notifications cannot be dismissed</i>
      </b-form-group>

      <b-form-group label="Start Date">
        <b-form-datepicker
            v-model="formData.start_date"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            locale="en-US"
            label-no-date-selected="No start date"
            today-button
            reset-button
            close-button
            label-today-button="Today"
            label-reset-button="Clear"
            :state="fieldValid('start_date')"
            class="notification-datepicker"
            :disabled="isDisabled"
        />
        <b-form-invalid-feedback :state="fieldValid('start_date')">
          {{ getFieldError('start_date') }}
        </b-form-invalid-feedback>
        <i>Leave empty to activate notification immediately</i>
      </b-form-group>

      <b-form-group label="End Date">
        <b-form-datepicker
            v-model="formData.end_date"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            locale="en-US"
            label-no-date-selected="No end date"
            today-button
            reset-button
            close-button
            label-today-button="Today"
            label-reset-button="Clear"
            :state="fieldValid('end_date')"
            class="notification-datepicker"
            :disabled="isDisabled"
        />
        <b-form-invalid-feedback :state="fieldValid('end_date')">
          {{ getFieldError('end_date') }}
        </b-form-invalid-feedback>
        <i>Leave empty to keep notification active forever</i>
      </b-form-group>

      <no-permission-wrapper :show="forbidden" v-if="!deleted">
        <b-button type="submit" variant="primary" :disabled="formProcessing || isDisabled" @click="submitForm">
          Save Notification
          <b-spinner v-if="formProcessing" class="ml-1" small/>
        </b-button>
      </no-permission-wrapper>
      <b-btn type="button" variant="secondary" @click.prevent="modalOpened = false">Cancel</b-btn>
    </b-form>
  </b-modal>
</template>

<script>
import NoPermissionWrapper from "./NoPermissionWrapper.vue";

export default {
  name: "AdminAppNotificationModal",
  components: {NoPermissionWrapper},
  emits: ['update:visible', 'input', 'submit'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object
    },
    formValidation: {
      type: Object
    },
    formProcessing: {
      type: Boolean,
      default: false
    },
    forbidden: {
      type: Boolean,
      default: false
    },
    deleted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editorToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
        ["blockquote", "code-block", "link", { color: [] }],
        ["clean"]
      ]
    }
  },
  computed: {
    modalOpened: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    isDisabled() {
      return this.forbidden || this.deleted;
    },
    formData: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    levelBtnVariant() {
      if (!this.formData.level) {
        return 'secondary';
      } else if (this.formData.level === 'error') {
        return 'danger';
      }

      return this.formData.level;
    },
    levelBtnText() {
      return !this.formData.level ? 'Default' : this.formData.level.charAt(0).toUpperCase() + this.formData.level.slice(1);
    }
  },
  methods: {
    submitForm() {
      this.$emit('submit');
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    getFieldError(field) {
      return typeof this.formValidation[field] !== "undefined" ? this.formValidation[field] : "";
    }
  }
}
</script>

<style lang="scss">
#admin-app-notification-modal{
  @media (min-width: 576px) {
    .modal-dialog {
      max-width: 520px;
    }
  }
}
.level-btn{
  .dropdown-toggle{
    &.btn-danger{
      color: #fff!important;
      background: #C62828!important;
    }
    &.btn-warning{
      color: #fff!important;
      background: #EF6C00!important;
    }
    &.btn-info{
      color: #fff!important;
      background: #1565C0!important;
    }
    &.btn-success{
      color: #fff!important;
      background: #2E7D32!important;
    }
  }
}
.level-btn-item{
  color: #fff!important;
  &.bg-danger{
    background: #C62828!important;
  }
  &.bg-warning{
    background: #EF6C00!important;
  }
  &.bg-info{
    background: #1565C0!important;
  }
  &.bg-success{
    background: #2E7D32!important;
  }
}
.notification-datepicker{
  .form-control{
    padding-top: 12px;
  }
}
.notification-content-preview{
  border: 1px solid #ced4da;
  padding: 5px;
}
</style>
